import { useTranslation } from "react-i18next";
import { Card } from "@/design-system";
import { ChangePasswordForm } from "@/components/ChangePasswordForm";
import { ProfileForm } from "@/components/ProfileForm";
import screen from "@/layout/screen.module.css";

/**
 * Account settings — reachable from the SideNav by any signed-in role
 * (`/settings`, guarded by `RequireAuth` in `App.tsx`).
 *
 * Two independent cards:
 *   - **Profile** (issue #224): edit your own display name via
 *     `PATCH /api/me`. No password involved, no session rotation.
 *   - **Password** (issue #152): change the password, which rotates
 *     every session token and swaps in a fresh one.
 *
 * Keeping them in separate cards makes the orthogonality explicit.
 */
export function SettingsScreen() {
  const { t } = useTranslation();

  return (
    <div
      className={screen.capped}
      style={{
        padding: "var(--space-6) var(--space-4)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-5)",
      }}
    >
      <div
        style={{
          font: "var(--fw-extra) var(--fs-2xl)/1.1 var(--font-display)",
          color: "var(--text-strong)",
          letterSpacing: "var(--ls-tight)",
        }}
      >
        {t("settings.title")}
      </div>

      <Card padding="var(--space-5)">
        <SectionHeading>{t("settings.profileTitle")}</SectionHeading>
        <ProfileForm />
      </Card>

      <Card padding="var(--space-5)">
        <SectionHeading>{t("settings.passwordTitle")}</SectionHeading>
        <ChangePasswordForm />
      </Card>
    </div>
  );
}

/** Small display-face heading that separates the two settings cards. */
function SectionHeading({ children }: { children: string }) {
  return (
    <div
      style={{
        font: "var(--fw-bold) var(--fs-lg)/1.1 var(--font-display)",
        color: "var(--text-strong)",
        marginBottom: "var(--space-4)",
      }}
    >
      {children}
    </div>
  );
}
